import { displayName } from '../../types';
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, FormControlLabel, Checkbox, MenuItem,
  Select, FormControl, InputLabel, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Tooltip, Tabs, Tab
} from '@mui/material';
import { Add, CheckCircle, Cancel, FlightTakeoff, Visibility, East } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';

const STATUS_COLORS: any = {
  pending_manager: 'warning', approved: 'success', rejected: 'error', cancelled: 'default'
};

const STATUS_LABELS: any = {
  pending_manager: 'Pending Manager', approved: 'Approved', rejected: 'Rejected', cancelled: 'Cancelled'
};

const CURRENCIES = ['HKD', 'USD', 'EUR', 'GBP', 'CNY', 'SGD', 'JPY'];

const BusinessTrips: React.FC = () => {
  const { user } = useAuth();
  const [trips, setTrips] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [openForm, setOpenForm] = useState(false);
  const [openApprove, setOpenApprove] = useState<any>(null);
  const [openDetail, setOpenDetail] = useState<any>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isAdmin = ['hr_admin', 'hr_manager', 'manager'].includes(user?.role || '');
  const isApprover = user?.employee?.access === 'approver';

  const [form, setForm] = useState({
    originCity: 'Hong Kong',
    destination: '', purpose: '', departureDate: '', returnDate: '',
    estimatedBudget: '', currency: 'HKD', accommodationNeeded: false, notes: ''
  });

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiService.getBusinessTrips();
      setTrips(data);
    } catch { setError('Failed to load trips'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = tab === 0 ? trips
    : tab === 1 ? trips.filter(t => t.status === 'pending_manager')
    : tab === 2 ? trips.filter(t => t.status === 'approved')
    : tab === 3 ? trips.filter(t => t.status === 'rejected')
    : trips.filter(t => t.status === 'cancelled');

  const handleSubmit = async () => {
    try {
      await apiService.createBusinessTrip({
        ...form,
        estimatedBudget: form.estimatedBudget ? Number(form.estimatedBudget) : undefined,
      });
      setSuccess('Trip request submitted!');
      setOpenForm(false);
      setForm({ originCity: 'Hong Kong', destination: '', purpose: '', departureDate: '', returnDate: '', estimatedBudget: '', currency: 'HKD', accommodationNeeded: false, notes: '' });
      load();
    } catch { setError('Failed to submit request'); }
  };

  const handleApprove = async (action: 'approve' | 'reject') => {
    try {
      await apiService.approveBusinessTrip(openApprove.id, { action, rejectionReason: rejectionReason || undefined });
      setSuccess(`Trip ${action === 'approve' ? 'approved' : 'rejected'}!`);
      setOpenApprove(null); setRejectionReason('');
      load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to update trip');
    }
  };

  const handleCancel = async (id: string) => {
    if (!window.confirm('Cancel this trip request?')) return;
    try {
      await apiService.cancelBusinessTrip(id);
      setSuccess('Trip cancelled');
      load();
    } catch { setError('Failed to cancel'); }
  };

  // Can this user approve/reject a specific trip?
  // Manager: if they are the direct manager of the trip's employee
  // HR Admin: can always action
  const canApprove = (trip: any) =>
    trip.status === 'pending_manager' && (
      isAdmin ||
      (trip.employee?.managerId && trip.employee.managerId === user?.employee?.id)
    );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight="bold">Business Trips ✈️</Typography>
          <Typography variant="body2" color="text.secondary">Request and manage business travel</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpenForm(true)}>
          New Request
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label={`All (${trips.length})`} />
          <Tab label={`Pending Manager (${trips.filter(t => t.status === 'pending_manager').length})`} />
          <Tab label={`Approved (${trips.filter(t => t.status === 'approved').length})`} />
          <Tab label={`Rejected (${trips.filter(t => t.status === 'rejected').length})`} />
          <Tab label={`Cancelled (${trips.filter(t => t.status === 'cancelled').length})`} />
        </Tabs>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>

                {(isAdmin || isApprover) && <TableCell>Employee</TableCell>}
                <TableCell>Destination</TableCell>
                <TableCell>Purpose</TableCell>
                <TableCell>Dates</TableCell>
                <TableCell>Budget</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No trips found</TableCell></TableRow>
              ) : filtered.map(trip => (
                <TableRow key={trip.id} hover>

                  {(isAdmin || isApprover) && (
                    <TableCell>{trip.employee ? displayName(trip.employee) : '—'}</TableCell>
                  )}
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <FlightTakeoff fontSize="small" sx={{ color: '#C08A2E' }} />
                      <Typography variant="body2">{trip.originCity || 'Hong Kong'}</Typography>
                      <East fontSize="small" sx={{ color: 'text.secondary', mx: 0.25 }} />
                      <Typography variant="body2" fontWeight={500}>{trip.destination}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography variant="body2" noWrap>{trip.purpose}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{trip.departureDate}</Typography>
                    <Typography variant="caption" color="text.secondary">→ {trip.returnDate}</Typography>
                  </TableCell>
                  <TableCell>
                    {trip.estimatedBudget ? `${trip.currency} ${Number(trip.estimatedBudget).toLocaleString()}` : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip label={STATUS_LABELS[trip.status] || trip.status} color={STATUS_COLORS[trip.status]} size="small" sx={{ fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View details">
                      <IconButton size="small" onClick={() => setOpenDetail(trip)}><Visibility fontSize="small" /></IconButton>
                    </Tooltip>

                    {(isAdmin || (isApprover && trip.employee?.managerId === user?.employee?.id)) && ['pending_manager','pending_cfo'].includes(trip.status) && (
                      <Tooltip title="Review">
                        <IconButton size="small" color="primary" onClick={() => setOpenApprove(trip)}><CheckCircle fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    {trip.employeeId === user?.employee?.id && trip.status === 'pending_manager' && (
                      <Tooltip title="Cancel">
                        <IconButton size="small" color="error" onClick={() => handleCancel(trip.id)}><Cancel fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* New Request Dialog */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Business Trip Request ✈️</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Route: From → To */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Departing From *"
                placeholder="e.g. Hong Kong"
                value={form.originCity}
                onChange={e => setForm({ ...form, originCity: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Destination *"
                placeholder="e.g. Singapore"
                value={form.destination}
                onChange={e => setForm({ ...form, destination: e.target.value })}
              />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth multiline rows={2} label="Purpose *" value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth type="date" label="Departure Date *" InputLabelProps={{ shrink: true }} value={form.departureDate} onChange={e => setForm({ ...form, departureDate: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth type="date" label="Return Date *" InputLabelProps={{ shrink: true }} value={form.returnDate} onChange={e => setForm({ ...form, returnDate: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Estimated Budget" type="number" value={form.estimatedBudget} onChange={e => setForm({ ...form, estimatedBudget: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select value={form.currency} label="Currency" onChange={e => setForm({ ...form, currency: e.target.value })}>
                  {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControlLabel control={<Checkbox checked={form.accommodationNeeded} onChange={e => setForm({ ...form, accommodationNeeded: e.target.checked })} />} label="Accommodation needed" />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth multiline rows={2} label="Additional Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!form.originCity || !form.destination || !form.purpose || !form.departureDate || !form.returnDate}>
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve/Reject Dialog */}
      <Dialog open={!!openApprove} onClose={() => setOpenApprove(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Review Trip Request</DialogTitle>
        <DialogContent>
          {openApprove && (
            <Box>
              {openApprove.employee && (
                <Typography variant="body1" gutterBottom><strong>Employee:</strong> {displayName(openApprove.employee)}</Typography>
              )}
              <Typography variant="body1" gutterBottom>
                <strong>Route:</strong> {openApprove.originCity || 'Hong Kong'} → {openApprove.destination}
              </Typography>
              <Typography variant="body1" gutterBottom><strong>Purpose:</strong> {openApprove.purpose}</Typography>
              <Typography variant="body1" gutterBottom><strong>Dates:</strong> {openApprove.departureDate} → {openApprove.returnDate}</Typography>
              {openApprove.estimatedBudget && <Typography variant="body1" gutterBottom><strong>Budget:</strong> {openApprove.currency} {Number(openApprove.estimatedBudget).toLocaleString()}</Typography>}
              {openApprove.notes && <Typography variant="body1" gutterBottom><strong>Notes:</strong> {openApprove.notes}</Typography>}
              <TextField fullWidth multiline rows={2} label="Rejection reason (required if rejecting)" value={rejectionReason}
                onChange={e => setRejectionReason(e.target.value)} sx={{ mt: 2 }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenApprove(null)}>Close</Button>
          <Button color="error" variant="outlined" onClick={() => handleApprove('reject')} disabled={!rejectionReason.trim()}>Reject</Button>
          <Button color="success" variant="contained" onClick={() => handleApprove('approve')}>Approve ✓</Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!openDetail} onClose={() => setOpenDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Trip Details</DialogTitle>
        <DialogContent>
          {openDetail && (
            <Box sx={{ '& > *': { mb: 1.5 } }}>
              {openDetail.employee && (
                <Typography><strong>Employee:</strong> {displayName(openDetail.employee)}</Typography>
              )}
              <Typography>
                <strong>Route:</strong> {openDetail.originCity || 'Hong Kong'} → {openDetail.destination}
              </Typography>
              <Typography><strong>Purpose:</strong> {openDetail.purpose}</Typography>
              <Typography><strong>Dates:</strong> {openDetail.departureDate} → {openDetail.returnDate}</Typography>
              <Typography><strong>Budget:</strong> {openDetail.estimatedBudget ? `${openDetail.currency} ${Number(openDetail.estimatedBudget).toLocaleString()}` : 'Not specified'}</Typography>
              <Typography><strong>Accommodation:</strong> {openDetail.accommodationNeeded ? 'Yes' : 'No'}</Typography>
              {openDetail.notes && <Typography><strong>Notes:</strong> {openDetail.notes}</Typography>}
              <Box><Chip label={STATUS_LABELS[openDetail.status] || openDetail.status} color={STATUS_COLORS[openDetail.status]} sx={{ fontWeight: 600 }} /></Box>
              {openDetail.managerApprovedAt && <Typography variant="body2" color="success.main">✅ Manager approved on {new Date(openDetail.managerApprovedAt).toLocaleDateString()}</Typography>}
              {openDetail.rejectionReason && <Alert severity="error"><strong>Rejection reason:</strong> {openDetail.rejectionReason}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BusinessTrips;
